import { useEffect } from 'react';
import { monitoringService } from '@/services/monitoringService';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';

export function useMonitoring(pageName: string) {
  const { toast } = useToast();

  useEffect(() => {
    let isSubscribed = true;
    const startTime = performance.now();

    const trackPageLoad = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session?.user) {
          console.log('User not authenticated, skipping performance tracking');
          return;
        }

        const loadTime = performance.now() - startTime;
        const memoryUsage = (window as any).performance?.memory?.usedJSHeapSize / (1024 * 1024) || 0;

        if (isSubscribed) {
          await monitoringService.trackPagePerformance({
            page_name: pageName,
            load_time_ms: Math.round(loadTime),
            memory_usage_mb: Math.round(memoryUsage * 100) / 100
          });
        }
      } catch (error: any) {
        console.error('Error tracking performance:', error);
        if (error.message?.includes('row-level security policy')) {
          // Silently ignore RLS policy violations during logout
          return;
        }
        toast({
          variant: "destructive",
          title: "Erro ao monitorar performance",
          description: "Não foi possível registrar métricas de performance."
        });
      }
    };

    // Track initial page load
    trackPageLoad();

    // Setup performance observer for subsequent renders
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach(async (entry) => {
        if (entry.entryType === 'render') {
          try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session?.user) return;

            const memoryUsage = (window as any).performance?.memory?.usedJSHeapSize / (1024 * 1024) || 0;
            
            if (isSubscribed) {
              await monitoringService.trackPagePerformance({
                page_name: pageName,
                load_time_ms: Math.round(entry.duration),
                memory_usage_mb: Math.round(memoryUsage * 100) / 100
              });
            }
          } catch (error: any) {
            if (error.message?.includes('row-level security policy')) {
              // Silently ignore RLS policy violations during logout
              return;
            }
            console.error('Error tracking performance:', error);
          }
        }
      });
    });

    observer.observe({ entryTypes: ['render'] });

    return () => {
      isSubscribed = false;
      observer.disconnect();
    };
  }, [pageName, toast]);
}